/**
 * 金流服務介面定義
 * 金流服務介面定義
 * 敏感金鑰與簽章皆由後端處理
 */

import { apiClient } from './client'

// ============= 通用類型定義 =============

export type PaymentProvider = 'neweb_pay' | 'ecpay' | 'paypal'

export type PaymentMethod = 
  | 'credit_card'      // 信用卡
  | 'ecpay_cvs'        // 綠界超商代碼繳費
  | 'ecpay_atm'        // 綠界 ATM 轉帳
  | 'ecpay_barcode'    // 綠界超商條碼繳費
  | 'paypal'           // PayPal

export type PaymentStatus = 
  | 'pending'          // 待付款
  | 'processing'       // 處理中
  | 'paid'             // 已付款
  | 'failed'           // 失敗
  | 'cancelled'        // 已取消
  | 'refunded'         // 已退款

export interface PaymentRequest {
  orderId: string
  amount: number
  currency: string
  provider: PaymentProvider
  method: PaymentMethod
  returnUrl: string
  notifyUrl: string
  payerEmail?: string
  description?: string
}

export interface PaymentResponse {
  transactionId: string
  merchantTradeNo?: string
  status: PaymentStatus
  paymentUrl?: string      // 導向支付頁面的 URL
  formData?: Record<string, string>  // 用於 POST 表單的數據（NewebPay）
  message?: string
}

export interface PaymentVerification {
  transactionId: string
  merchantTradeNo?: string
  orderId: string
  amount: number
  currency: string
  status: PaymentStatus
  paidAt?: string
  provider: PaymentProvider
  externalReference?: string
  failureReason?: string
}

// ============= NewebPay (藍新金流) 相關 =============

export interface NewebPayFormData {
  MerchantID: string
  TradeInfo: string
  TradeSha: string
  Version: string
  EncryptType?: string
}

export interface ECPayCallbackData {
  MerchantID: string
  MerchantTradeNo: string
  StoreID?: string
  RtnCode: string          // 1 = 成功
  RtnMsg: string
  TradeNo: string
  TradeAmt: string
  PaymentDate: string
  PaymentType: string
  CheckMacValue: string
  // 更多欄位參考綠界文檔
}

// ============= PayPal 相關 =============

export interface PayPalConfig {
  clientId: string
  clientSecret: string
  mode: 'sandbox' | 'live'
}

export interface PayPalOrderResponse {
  id: string
  status: string
  links: Array<{
    href: string
    rel: string
    method: string
  }>
}

export interface PayPalCaptureResponse {
  id: string
  status: string
  purchase_units: Array<{
    payments: {
      captures: Array<{
        id: string
        status: string
        amount: {
          currency_code: string
          value: string
        }
      }>
    }
  }>
}

// ============= Payment API 服務 =============

/**
 * 金流 API 服務
 * TODO: 實際實現需在後端完成以保護敏感資訊（MerchantID, HashKey 等）
 */
export const paymentsApi = {
  async createPayment(request: PaymentRequest): Promise<PaymentResponse> {
    return apiClient.post<PaymentResponse>('/api/payments/create', request)
  },

  /**
   * 驗證支付結果（ECPay 回調）
   * TODO: 後端接收綠界 ReturnURL 回調，驗證 CheckMacValue
   */
  async verifyECPayCallback(data: ECPayCallbackData): Promise<PaymentVerification> {
    return apiClient.post<PaymentVerification>('/api/payments/ecpay/verify', data)
  },

  /**
   * 創建 PayPal 訂單
   * TODO: 後端使用 PayPal SDK 創建訂單
   */
  async createPayPalOrder(orderId: string, amount: number): Promise<PayPalOrderResponse> {
    return apiClient.post<PayPalOrderResponse>('/api/payments/paypal/create-order', {
      orderId,
      amount,
    })
  },

  /**
   * 捕獲 PayPal 付款
   * TODO: 後端使用 PayPal SDK 捕獲已授權的付款
   */
  async capturePayPalOrder(paypalOrderId: string): Promise<PayPalCaptureResponse> {
    return apiClient.post<PayPalCaptureResponse>('/api/payments/paypal/capture', {
      orderID: paypalOrderId,
    })
  },

  /**
   * 查詢支付狀態
   * TODO: 連接後台 API
   */
  async getPaymentStatus(transactionId: string): Promise<PaymentVerification> {
    return apiClient.get<PaymentVerification>(`/api/payments/${transactionId}`)
  },

  /**
   * 申請退款
   * TODO: 連接後台 API，後端調用綠界或 PayPal 退款 API
   */
  async refund(transactionId: string, amount?: number): Promise<PaymentVerification> {
    return apiClient.post<PaymentVerification>(`/api/payments/${transactionId}/refund`, {
      amount,
    })
  },
}

// ============= 環境變數配置範例 =============

/**
 * 注意：正式環境部署時，NewebPay MerchantID / HashKey / HashIV 只能存放於後端。
 */
